import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Popconfirm,
  Row,
  Space
} from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { debounce, isEmpty } from "lodash";
import { useLocation, useNavigate } from "react-router-dom";
import AgencySelectEx from "../../../shared/form/AgencySelectEx";
import { getInitialAgency } from "../../../shared/form/getInitialAgency";
import { agenciesListEqual } from "../../../shared/sorting";
import { salesVolumeAndThresholdsSchema } from "../salesVolumeAndThresholds/schemas";
import AdvancedRangePicker from "../../../shared/form/reactHookForm/AdvancedRangePicker";
import { dateFormat, toTZNeutralFormat } from "../../../shared/formatting";
import { fixedDateRanges, dateRangeConfigs } from "../../../shared/form/fixedDateRanges";
import dayjs from "dayjs";
import {
  shortLabelLongInputLayoutResponsive as layout
} from "../../../shared/form/formLayout";
import {
  listSaleVolumeAndThresholdsAction,
  updateThresholdsAction,
  clearOperationResultAction,
} from "../../../../store/inventory/inventoryActions";
import { tableNames } from "../../../shared/table/tableNames";
import { SearchableTable } from "../../../shared/table/SearchableTable";
import { getSalesVolumeAndThresholdsColumns } from "../salesVolumeAndThresholds/columns";
import openNotificationOperationResult from "../../../shared/openNotificationOperationResult";
import useUserPreferences from "../../../../hooks/useUserPreferences";
import { SaveOutlined, UndoOutlined } from "@ant-design/icons";
import {
  parseBoolParam,
  getTableScroll,
} from "./utils";

const initialValues = {
  AgencyKey: "",
  FixedDateRange: fixedDateRanges.LAST_X_DAYS,
  NumberOfDays: 365,
  IssuedDate: [
    toTZNeutralFormat(dayjs().subtract(364, "days").startOf("day")),
    toTZNeutralFormat(dayjs().endOf("day")),
  ],
  ShowBelowThreshold: true,
  ShowAtOrAboveThreshold: true,
  ShowWithNoThreshold: true,
};

const SalesVolumeAndThresholdsForm = ({ agencies, homeAgency }) => {
  const {
    control,
    reset,
    setValue,
    watch,
    getValues,
    trigger
  } = useForm({
    mode: "onChange",
    defaultValues: initialValues,
    resolver: yupResolver(salesVolumeAndThresholdsSchema),
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const brokerId = useSelector((state) => state.actor.details.data.BrokerId);
  const list = useSelector((state) => state.inventory.salesVolumeAndThresholds.data);
  const loading = useSelector((state) => state.inventory.salesVolumeAndThresholds.loading);
  const operationResult = useSelector(state => state.inventory.operationResult);
  const [currentAgencies, setCurrentAgencies] = useState([]);
  const [formIsValid, setFormIsValid] = useState(true);
  const [editedThresholds, setEditedThresholds] = useState({});
  const [resetConfirmVisible, setResetConfirmVisible] = useState(false);
  const tableRef = useRef(null);
  const formValues = watch();
  const watchedFilters = useWatch({
    control,
    name: [
      "AgencyKey", // It's important that AgencyKey stay as first index.
      "IssuedDate",
      "ShowBelowThreshold",
      "ShowAtOrAboveThreshold",
      "ShowWithNoThreshold"
    ]
  });

  const {
    preferences,
    setEditableUserPreference,
    saveUserPreferences,
    loading: userPreferencesLoading,
  } = useUserPreferences([
    "DisplayLowInventoryAlertHomePage",
    "NotifyLowInventoryByEmail",
  ]);

  const refreshData = (values) => {
    dispatch(listSaleVolumeAndThresholdsAction(values));
  };

  const refreshDataIfValid = async (values) => {
    const isValid = await trigger();
    setFormIsValid(isValid);
    if (isValid) {
      refreshData(values);
    }
  };

  const debouncedRefreshData = useMemo(
    () => debounce(refreshDataIfValid, 1000),
    [trigger]
  );

  const getFilterParamsFromQuery = (params) => ({
    agencyKey: params.get("agencyKey"),
    showBelowThreshold: parseBoolParam(params.get("showBelowThreshold"), true),
    showAtOrAboveThreshold: parseBoolParam(params.get("showAtOrAboveThreshold"), true),
    showWithNoThreshold: parseBoolParam(params.get("showWithNoThreshold"), true),
  });

  const buildInitialFormValues = (agencies, homeAgency, queryParams) => {
    const initialAgency = getInitialAgency(homeAgency, agencies);
    const filterParams = getFilterParamsFromQuery(queryParams);

    return {
      ...initialValues,
      AgencyKey: filterParams.agencyKey
        ? Number(filterParams.agencyKey)
        : initialAgency,
      ShowBelowThreshold: filterParams.showBelowThreshold,
      ShowAtOrAboveThreshold: filterParams.showAtOrAboveThreshold,
      ShowWithNoThreshold: filterParams.showWithNoThreshold,
    };
  };

  useEffect(() => {
    return () => {
      debouncedRefreshData.cancel();
    };
  }, []);

  useEffect(() => {
    if (!agencies || isEmpty(agencies) || agenciesListEqual(currentAgencies, agencies)) return;

    setCurrentAgencies(agencies);

    const defaults = buildInitialFormValues(agencies, homeAgency, queryParams);

    reset(defaults);
    debouncedRefreshData.cancel();

    trigger().then((isValid) => {
      if (isValid) {
        refreshDataIfValid(defaults).catch(console.error);
      }
    });
  }, [agencies, homeAgency, queryParams]);

  useEffect(() => {
    const [
      AgencyKey,
      IssuedDate,
      ShowBelowThreshold,
      ShowAtOrAboveThreshold,
      ShowWithNoThreshold
    ] = watchedFilters;

    if (!AgencyKey || !IssuedDate?.[0] || !IssuedDate?.[1]) return;

    const values = {
      AgencyKey,
      IssuedDate,
      ShowBelowThreshold,
      ShowAtOrAboveThreshold,
      ShowWithNoThreshold,
      FixedDateRange: getValues("FixedDateRange"),
      NumberOfDays: getValues("NumberOfDays"),
    };

    debouncedRefreshData(values);
  }, [watchedFilters]);

  useEffect(() => {
    trigger().then((isValid) => setFormIsValid(isValid));
  }, [watchedFilters]);

  useEffect(() => {
    if (list?.length) {
      setEditedThresholds({});
    }
  }, [list]);

  useEffect(() => {
    openNotificationOperationResult(operationResult, () =>
      dispatch(clearOperationResultAction())
    );
  }, [operationResult, dispatch]);

  const handleUpdateAlertSettings = () => {
    saveUserPreferences(
      {
        DisplayLowInventoryAlertHomePage: preferences.DisplayLowInventoryAlertHomePage,
        NotifyLowInventoryByEmail: preferences.NotifyLowInventoryByEmail
      },
      brokerId
    );
  };

  const issuedDate = formValues.IssuedDate;
  const rangeDays = issuedDate?.[0] && issuedDate?.[1]
    ? dayjs(issuedDate[1]).diff(dayjs(issuedDate[0]), "day") + 1
    : 0;

  const showPrior30 = rangeDays >= 30;
  const showPrior90 = rangeDays >= 90;
  const showPrior180 = rangeDays >= 180;

  const buildThresholdUpdates = (thresholds, agencyKey, list) =>
    Object.entries(thresholds)
      .map(([PlateTypeKey, edited]) => {
        const row = list.find((r) => r.PlateTypeKey === Number(PlateTypeKey));
        const original = row?.Threshold ?? null;
        const newValue =
          edited === "" || edited == null || Number(edited) === 0
            ? null
            : Number(edited);

        return {
          agencyKey,
          PlateTypeKey: Number(PlateTypeKey),
          Threshold: newValue,
          original,
        };
      })
      .filter(({ Threshold, original }) => Threshold !== original)
      .map(({ agencyKey, PlateTypeKey, Threshold }) => ({
        AgencyKey: agencyKey,
        PlateTypeKey,
        Threshold,
      }));

  const submitThresholdUpdates = (updates, values) => {
    dispatch(updateThresholdsAction(updates))
      .then(({ Operation }) => {
        if (Operation?.Outcome === "Success") {
          refreshDataIfValid(values).catch(console.error);
        }
      });
  };

  const handleUpdateThresholds = () => {
    const { AgencyKey } = formValues;
    if (!AgencyKey) return;

    const updates = buildThresholdUpdates(editedThresholds, AgencyKey, list);
    if (updates.length === 0) return;

    submitThresholdUpdates(updates, formValues);
  };

  const handleUserPreferenceCheckboxChange = (name) => (e) => {
    setEditableUserPreference(name, e.target.checked);
  };

  const columns = getSalesVolumeAndThresholdsColumns({
    showPrior30,
    showPrior90,
    showPrior180,
    editedThresholds,
    setEditedThresholds,
  });

  const hasChangesToNewThresholds = () => {
    if (!Array.isArray(list)) return false;

    return Object.entries(editedThresholds).some(([plateTypeKey, newValue]) => {
      const original = list.find(r => r.PlateTypeKey === Number(plateTypeKey))?.Threshold ?? null;
      return (newValue ?? null) !== (original ?? null);
    });
  };

  // `handleReset = ()` should come before `const resetButton`
  const handleReset = () => {
    // Clear query params from URL (removes ?agencyKey= etc.)
    navigate("/salesVolumeAndThresholds", { replace: true });

    const defaults = buildInitialFormValues(agencies, homeAgency, new URLSearchParams());

    // Reset form to defaults
    reset(defaults);
    setEditedThresholds({});

    // Clear table filters & sorting
    if (tableRef.current?.resetFilters) {
      tableRef.current.resetFilters();
    }

    // Trigger data reload
    debouncedRefreshData.cancel();
    refreshDataIfValid(defaults).catch(console.error);
  };

  const shouldConfirmReset = (() => {
    const watchedAgency = watchedFilters?.[0];
    const initialAgency = getInitialAgency(homeAgency, agencies);
    return (
      watchedAgency != null &&
      Number(watchedAgency) !== Number(initialAgency)
    );
  })();

  const onResetClick = () => {
    if (shouldConfirmReset) {
      setResetConfirmVisible(true);
    } else {
      handleReset();
    }
  };

  const onConfirmReset = () => {
    setResetConfirmVisible(false);
    handleReset();
  };

  const onCancelReset = () => {
    setResetConfirmVisible(false);
  };

  const resetButtonWithConfirmation = (
    <Popconfirm
      title="Reset Form and Table"
      description="Resetting will change the selected agency back to your home agency. Are you sure?"
      open={resetConfirmVisible}
      onConfirm={onConfirmReset}
      onCancel={onCancelReset}
      okText="Yes"
      cancelText="No"
    >
      <Button
        size="small"
        type="dashed"
        icon={<UndoOutlined />}
        onClick={onResetClick}
      >
        Reset
      </Button>
    </Popconfirm>
  );

  return (
    <Row>
      <Col span={24}>
        <Form size="small" autoComplete="off">
          <Row gutter={16}>
            <Col xs={24} sm={24} md={24} lg={24} xl={12} xxl={10}>
              <AgencySelectEx
                isLoading={currentAgencies.length === 0}
                control={control}
                layout={layout}
                name="AgencyKey"
                required={true}
                formLabel="Agency"
                agencies={currentAgencies}
              />

              <Form.Item
                labelCol={layout.labelCol}
                wrapperCol={layout.wrapperCol}
                label="Include"
                required
                style={{marginTop: "16px"}}
              >
                <Space direction="vertical">
                  <Controller
                    control={control}
                    name="ShowBelowThreshold"
                    render={({field}) => (
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => setValue("ShowBelowThreshold", e.target.checked)}
                      >
                        Plates below threshold level
                      </Checkbox>
                    )}
                  />
                  <Controller
                    control={control}
                    name="ShowAtOrAboveThreshold"
                    render={({field}) => (
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => setValue("ShowAtOrAboveThreshold", e.target.checked)}
                      >
                        Plates at or above threshold level
                      </Checkbox>
                    )}
                  />
                  <Controller
                    control={control}
                    name="ShowWithNoThreshold"
                    render={({field}) => (
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => setValue("ShowWithNoThreshold", e.target.checked)}
                      >
                        Plates with no threshold defined
                      </Checkbox>
                    )}
                  />
                </Space>
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24} lg={24} xl={12} xxl={10}>
              <AdvancedRangePicker
                formItem={{
                  layout: layout,
                  required: true,
                  label: "Plates Sold Date Range:",
                }}
                control={control}
                allowClear={true}
                name="IssuedDate"
                format={dateFormat}
                dateRangeConfig={dateRangeConfigs.NON_TRANSACTION_NO_LAST_YEAR}
                setValue={setValue}
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
              />
            </Col>
          </Row>
        </Form>

        <SearchableTable
          tableName={tableNames.SalesVolumeAndThresholds}
          dataSource={formIsValid ? list : []}
          columns={columns}
          pagination={false}
          rowKey={(r) => r.PlateTypeKey}
          useRefreshState={false}
          tableLayout="fixed"
          scroll={getTableScroll(columns, showPrior180, showPrior90, showPrior30)}
          bordered
          customRef={tableRef}
          hideClearFiltersButtons={true}
          loading={loading}
          rowClassNameExtended={(record, defaultFunction) => {
            if (record.AboveThreshold != null && record.AboveThreshold < 0) {
              return "inventory-threshold-low";
            }
            return defaultFunction(record);
          }}
        />

        <Row justify="end" style={{ marginTop: 16, marginBottom: 24 }}>
          <Col>
            <Space>
              <Button
                size="small"
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleUpdateThresholds}
                disabled={!hasChangesToNewThresholds()}
              >
                Update Thresholds
              </Button>

              {resetButtonWithConfirmation}
            </Space>
          </Col>
        </Row>

        <Card style={{ marginTop: 24 }}>
          <b>When plate inventory is below threshold</b>
          <Divider style={{ margin: "12px 0" }} />

          <Row gutter={24} align="bottom">
            <Col xs={24} sm={18}>
              <Space direction="vertical" style={{ width: "100%" }}>
                {preferences && (
                  <>
                    <Checkbox
                      checked={!!preferences.DisplayLowInventoryAlertHomePage}
                      onChange={handleUserPreferenceCheckboxChange("DisplayLowInventoryAlertHomePage")}
                    >
                      Display alert on home page
                    </Checkbox>
                    <Checkbox
                      checked={!!preferences.NotifyLowInventoryByEmail}
                      onChange={handleUserPreferenceCheckboxChange("NotifyLowInventoryByEmail")}
                    >
                      Notify me by email
                    </Checkbox>
                  </>
                )}
              </Space>
            </Col>
            <Col xs={24} sm={6} style={{ textAlign: "right" }}>
              <Button
                type="default"
                onClick={handleUpdateAlertSettings}
                loading={userPreferencesLoading}
              >
                Update alert settings
              </Button>
            </Col>
          </Row>
        </Card>
      </Col>
    </Row>
  );
};

export default SalesVolumeAndThresholdsForm;
